import React, { useCallback, useMemo, useState } from 'react';

import { Input as AntInput, DatePicker, Form, Modal, Popover } from 'antd-v5';
import { FormattedMessage } from 'react-intl';
import { Button } from 'scouting-ui/lib';

import { toastService } from '@modules/toasts';
import { EditorTextArea } from '@shared/components';
import { intl } from '@shared/localization';

import styles from './AddBanner.styles.less';
import { useEventsCommunications } from './hooks';

function AddBanner() {
  const [isOpen, setIsOpen] = useState(false);
  const [form] = Form.useForm();
  const { loading, orgAnnouncement, postEvent } = useEventsCommunications();

  const openModal = useCallback(() => setIsOpen(true), []);
  const closeModal = useCallback(() => setIsOpen(false), []);

  const handleSave = useCallback(async () => {
    try {
      const { title, message, startDate, endDate } =
        await form.validateFields();

      await postEvent(
        title,
        message,
        startDate.startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
        endDate.endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
      );

      closeModal();
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'errorFields' in error) {
        toastService.error(
          intl.formatMessage({ id: 'core.banner.invalidFields' }),
        );
      }
    }
  }, [postEvent, closeModal, form]);

  const footer = useMemo(
    () => (
      <>
        <Button
          data-testid="close-banner-button"
          ghost
          onClick={closeModal}
          shape="round"
          size="small"
          type="primary"
        >
          <FormattedMessage id="shared.close" />
        </Button>
        <Button
          data-testid="save-banner-button"
          loading={loading}
          onClick={handleSave}
          shape="round"
          size="large"
          type="primary"
        >
          <FormattedMessage id="shared.save" />
        </Button>
      </>
    ),
    [closeModal, handleSave, loading],
  );

  return (
    <>
      <Modal
        centered
        footer={footer}
        loading={loading}
        onCancel={closeModal}
        open={isOpen}
        title={<FormattedMessage id="core.banner.modalTitle" />}
      >
        <Form
          clearOnDestroy
          form={form}
          initialValues={{}}
          className={styles.form}
        >
          <Form.Item
            name="title"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'shared.form.error.isRequired',
                }),
              },
            ]}
            noStyle
          >
            <AntInput placeholder={'Title'} />
          </Form.Item>
          <Form.Item
            name="message"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'shared.form.error.isRequired',
                }),
              },
            ]}
            noStyle
          >
            <EditorTextArea
              placeholder="Message"
              className={styles.editor}
              toolbar={{
                options: ['history', 'inline', 'link'],
              }}
            />
          </Form.Item>
          <div className={styles.dates}>
            <Form.Item
              name="startDate"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: 'shared.form.error.isRequired',
                  }),
                },
              ]}
              noStyle
            >
              <DatePicker placeholder={'Start Date'} />
            </Form.Item>
            <Form.Item
              name="endDate"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: 'shared.form.error.isRequired',
                  }),
                },
              ]}
              noStyle
            >
              <DatePicker placeholder={'End Date'} />
            </Form.Item>
          </div>
        </Form>
      </Modal>
      <Popover
        placement="bottom"
        content={
          orgAnnouncement ? (
            <FormattedMessage id={'core.banner.popover'} />
          ) : null
        }
      >
        <Button
          data-testid="add-banner-button"
          disabled={!!orgAnnouncement || loading}
          onClick={openModal}
          shape="round"
          size="small"
          type="primary"
        >
          <FormattedMessage id="core.banner.add" />
        </Button>
      </Popover>
    </>
  );
}

export default AddBanner;
